/**
 * ForecastWorkbookArchive — the saved weekly copies (quick-260922-mvc).
 *
 * John, 2026-09-22: a copy of the workbook is saved every Monday 09:00
 * America/New_York by the sync-worker. This lists them newest first with a
 * download link per week; the link is a plain anchor to the API's attachment
 * route (same SF-session cookie the page already carries), so nothing here
 * builds bytes or reads the Upside % — the archive is always at the stored
 * default. Renders nothing while loading, on error, or when empty: the list is
 * a convenience under the Download button, not a tile that can fail loudly.
 */
import type { ForecastWorkbookArchiveEntry } from '@saledash/shared';

interface ForecastWorkbookArchiveProps {
  workbooks: readonly ForecastWorkbookArchiveEntry[] | undefined;
}

function formatWeek(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

function formatKb(bytes: number): string {
  return `${String(Math.max(1, Math.round(bytes / 1024)))} KB`;
}

export function ForecastWorkbookArchive({ workbooks }: ForecastWorkbookArchiveProps): React.JSX.Element | null {
  if (!workbooks || workbooks.length === 0) return null;
  return (
    <details className="text-xs text-ink-500" data-testid="forecast-workbook-archive">
      <summary className="cursor-pointer select-none font-semibold text-ink-700">
        Saved weekly copies ({String(workbooks.length)})
      </summary>
      <ul className="mt-1 max-h-40 space-y-0.5 overflow-y-auto">
        {workbooks.map((w) => (
          <li key={w.id} className="flex items-baseline justify-between gap-3">
            <a
              href={`/api/forecast/workbooks/${String(w.id)}`}
              className="text-brand-700 underline-offset-2 hover:underline"
              data-testid={`forecast-workbook-archive-link-${String(w.id)}`}
            >
              Week of {formatWeek(w.week_start)}
            </a>
            <span className="tabular-nums text-ink-400">
              {w.period_labels.replace(',', ' · ')} · {formatKb(w.byte_length)}
            </span>
          </li>
        ))}
      </ul>
    </details>
  );
}
